import {
  normalizeProtocolV1LevelConfig,
  PROTOCOL_V1_VERSION,
} from './protocolV1Adapter';
import { WORLD_IDENTITY_V2 } from './worldIdentityV2';
import { WORLD_MECHANICS_VERSION } from './worldMechanicsV3';

const MAX_PROTOCOL_V1_ACTIONS = 200;

function replayError(code) {
  const error = new Error(code);
  error.code = code;
  return error;
}

function assertSerializable(value, code) {
  const seen = new Set();
  const visit = (current) => {
    if (current === undefined || typeof current === 'function' || typeof current === 'symbol') {
      throw replayError(code);
    }
    if (typeof current === 'number' && !Number.isFinite(current)) {
      throw replayError(code);
    }
    if (!current || typeof current !== 'object') return;
    if (seen.has(current)) throw replayError(code);
    seen.add(current);
    if (Array.isArray(current)) {
      current.forEach(visit);
    } else {
      Object.keys(current).forEach((key) => visit(current[key]));
    }
    seen.delete(current);
  };
  visit(value);
}

function cloneSerializable(value, code = 'invalid_replay_payload') {
  assertSerializable(value, code);
  return JSON.parse(JSON.stringify(value));
}

function canonicalPosition(position) {
  const row = position?.row;
  const col = position?.col;
  if (!Number.isInteger(row) || !Number.isFinite(row) ||
      !Number.isInteger(col) || !Number.isFinite(col)) {
    throw replayError('invalid_replay_action');
  }
  return { row, col };
}

function canonicalInitialBoard(initialBoard) {
  if (!Array.isArray(initialBoard) || initialBoard.length === 0) {
    throw replayError('invalid_replay_initial_board');
  }
  const cols = Array.isArray(initialBoard[0]) ? initialBoard[0].length : 0;
  if (!cols) throw replayError('invalid_replay_initial_board');
  return initialBoard.map((row) => {
    if (!Array.isArray(row) || row.length !== cols) throw replayError('invalid_replay_initial_board');
    return row.map((value) => {
      if (typeof value === 'string' && value) return value;
      if (!value || typeof value !== 'object' || Array.isArray(value)) {
        throw replayError('invalid_replay_initial_board');
      }
      if (value.hole === true) return { hole: true };
      if (typeof value.key !== 'string' || !value.key) {
        throw replayError('invalid_replay_initial_board');
      }
      return value.special
        ? { key: value.key, special: String(value.special) }
        : value.key;
    });
  });
}

function canonicalInitialMeta(initialMeta) {
  if (!initialMeta || typeof initialMeta !== 'object' || Array.isArray(initialMeta)) {
    throw replayError('invalid_replay_initial_meta');
  }
  const charge = initialMeta.charge;
  const chargeTarget = initialMeta.chargeTarget;
  if (!Number.isFinite(charge) || !Number.isFinite(chargeTarget)) {
    throw replayError('invalid_replay_initial_meta');
  }
  let hole = null;
  if (initialMeta.hole != null) {
    if (!Number.isInteger(initialMeta.hole.r) || !Number.isInteger(initialMeta.hole.c)) {
      throw replayError('invalid_replay_initial_meta');
    }
    hole = { r: initialMeta.hole.r, c: initialMeta.hole.c };
  }
  const result = { hole, charge, chargeTarget };
  if (initialMeta.worldMechanicsVersion != null) {
    if (Number(initialMeta.worldMechanicsVersion) !== WORLD_MECHANICS_VERSION || !Array.isArray(initialMeta.mechanicState)) {
      throw replayError('invalid_replay_mechanic_metadata');
    }
    result.worldMechanicsVersion = WORLD_MECHANICS_VERSION;
    result.mechanicState = initialMeta.mechanicState.map((row) => row.map((cell) => {
      if (cell == null) return null;
      if (!cell || !['volcanic', 'stone', 'water_layer'].includes(cell.type) || !Number.isInteger(cell.hp) || cell.hp < 1 || cell.hp > 4) {
        throw replayError('invalid_replay_mechanic_metadata');
      }
      return { type: cell.type, hp: cell.hp };
    }));
  }
  return result;
}

export function canonicalProtocolV1SwapAction(action) {
  if (!action || typeof action !== 'object' || Array.isArray(action) || action.type !== 'swap') {
    throw replayError('invalid_replay_action');
  }
  return {
    type: 'swap',
    from: canonicalPosition(action.from),
    to: canonicalPosition(action.to),
  };
}

export function buildProtocolV1ReplayPayload(input) {
  if (!input || typeof input !== 'object' || Array.isArray(input)) {
    throw replayError('invalid_replay_payload');
  }
  assertSerializable(input, 'invalid_replay_payload');
  const version = input.version ?? PROTOCOL_V1_VERSION;
  if (version !== PROTOCOL_V1_VERSION) throw replayError('unsupported_replay_version');
  if (typeof input.seed !== 'string' || !input.seed || input.seed.length > 128) {
    throw replayError('invalid_replay_seed');
  }
  if (!input.levelConfig || typeof input.levelConfig !== 'object' || Array.isArray(input.levelConfig)) {
    throw replayError('invalid_replay_level_config');
  }
  if (!Array.isArray(input.actions)) throw replayError('invalid_replay_actions');
  if (input.actions.length > MAX_PROTOCOL_V1_ACTIONS) {
    throw replayError('too_many_replay_actions');
  }

  const initialBoard = canonicalInitialBoard(input.initialBoard);
  const initialMeta = canonicalInitialMeta(input.initialMeta);
  const actions = input.actions.map(canonicalProtocolV1SwapAction);
  for (const action of actions) {
    const { from, to } = action;
    const inBounds = ({ row, col }) => (
      row >= 0 && col >= 0 && row < initialBoard.length && col < initialBoard[0].length
    );
    if (!inBounds(from) || !inBounds(to) ||
        Math.abs(from.row - to.row) + Math.abs(from.col - to.col) !== 1) {
      throw replayError('invalid_replay_action');
    }
  }
  const levelOptions = {};
  if (Number(input.levelConfig.worldIdentityVersion) === WORLD_IDENTITY_V2.version) levelOptions.worldIdentityVersion = WORLD_IDENTITY_V2.version;
  if (Number(input.levelConfig.worldMechanicsVersion) === WORLD_MECHANICS_VERSION) levelOptions.worldMechanicsVersion = WORLD_MECHANICS_VERSION;
  const payload = {
    version,
    seed: input.seed,
    levelConfig: normalizeProtocolV1LevelConfig(input.levelConfig.level, levelOptions),
    initialBoard,
    initialMeta,
    actions,
  };
  return cloneSerializable(payload);
}

export function createProtocolV1RunRecorder() {
  let snapshot = null;
  let ineligibleReason = null;
  const logicalAttempts = new Set();

  return Object.freeze({
    startRun(input) {
      const next = buildProtocolV1ReplayPayload({
        ...input,
        version: input?.version ?? PROTOCOL_V1_VERSION,
        actions: [],
      });
      const serialized = JSON.stringify(next);
      if (snapshot && JSON.stringify(snapshot) === serialized) return this.getPayload();
      snapshot = next;
      ineligibleReason = null;
      logicalAttempts.clear();
      return this.getPayload();
    },

    recordSwapAttempt(action, logicalAttemptId) {
      if (!snapshot) throw replayError('replay_run_not_initialized');
      if (logicalAttemptId !== undefined && logicalAttempts.has(logicalAttemptId)) return false;
      const canonical = canonicalProtocolV1SwapAction(action);
      if (snapshot.actions.length >= MAX_PROTOCOL_V1_ACTIONS) {
        throw replayError('too_many_replay_actions');
      }
      snapshot.actions.push(canonical);
      if (logicalAttemptId !== undefined) logicalAttempts.add(logicalAttemptId);
      return true;
    },

    getPayload() {
      if (!snapshot) throw replayError('replay_run_not_initialized');
      return cloneSerializable(snapshot);
    },

    getPayloadForTransport() {
      if (!snapshot || ineligibleReason) return null;
      return buildProtocolV1ReplayPayload(snapshot);
    },

    markIneligible(reason = 'unsupported_gameplay_action') {
      if (!snapshot) return false;
      ineligibleReason = String(reason || 'unsupported_gameplay_action');
      return true;
    },

    reset() {
      snapshot = null;
      ineligibleReason = null;
      logicalAttempts.clear();
    },
  });
}

export function getReplayPayloadForRunSubmission(recorder) {
  try {
    const payload = recorder?.getPayloadForTransport?.();
    return payload ? buildProtocolV1ReplayPayload(payload) : null;
  } catch (_) {
    return null;
  }
}

export { MAX_PROTOCOL_V1_ACTIONS };
